import React, { useCallback, useEffect, useRef, useState } from 'react'
import Icon from '../AppIcon'

export const PIN_MIN = 4
export const PIN_MAX = 8

const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back']

/**
 * Shared PIN entry pad (dots + keypad + submit) used by the portal lock screen
 * and the per-transaction operator PIN dialog. Owns the typed PIN locally;
 * `onSubmit(pin)` must verify (throw on failure — the thrown message is shown
 * and the pad clears either way).
 */
export default function PinKeypad({ onSubmit, submitLabel = 'Unlock', onBusyChange, minLength = PIN_MIN, maxLength = PIN_MAX }) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(false)
  // Synchronous double-submit guard: state updates are async, so a held Enter
  // key or fast double-click could invoke onSubmit twice before re-render.
  const submittingRef = useRef(false)

  const pressDigit = useCallback((digit) => {
    setError('')
    setPin((prev) => (prev.length >= maxLength ? prev : prev + digit))
  }, [maxLength])

  const pressBack = useCallback(() => {
    setError('')
    setPin((prev) => prev.slice(0, -1))
  }, [])

  const pressClear = useCallback(() => {
    setError('')
    setPin('')
  }, [])

  const submit = useCallback(async () => {
    if (submittingRef.current || pin.length < minLength) return
    submittingRef.current = true
    setVerifying(true)
    onBusyChange?.(true)
    setError('')
    try {
      await onSubmit(pin)
      setPin('')
    } catch (err) {
      setPin('')
      setError(err?.message || 'Invalid PIN')
    } finally {
      submittingRef.current = false
      setVerifying(false)
      onBusyChange?.(false)
    }
  }, [pin, onSubmit, onBusyChange, minLength])

  // Physical keyboard support
  useEffect(() => {
    const onKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key)) { pressDigit(e.key); return }
      if (e.key === 'Backspace') { pressBack(); return }
      if (e.key === 'Enter') {
        // A focused button handles Enter natively — don't double-fire.
        if (document.activeElement?.tagName === 'BUTTON') return
        submit()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [pressDigit, pressBack, submit])

  return (
    <>
      {/* PIN dots */}
      <div className="flex items-center justify-center gap-2.5 mb-4 min-h-[16px]" aria-label="PIN entry">
        {pin.length === 0 ? (
          <span className="text-xs text-muted-foreground/60">{minLength === maxLength ? `${minLength} digits` : `${minLength}-${maxLength} digits`}</span>
        ) : (
          Array.from({ length: pin.length }).map((_, i) => (
            <span key={i} className="w-3 h-3 rounded-full bg-primary" />
          ))
        )}
      </div>

      {error ? (
        <p className="text-xs font-medium text-destructive mb-4" role="alert">{error}</p>
      ) : (
        <div className="mb-4 h-4" />
      )}

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-2.5 mb-4">
        {KEYPAD.map((key) => {
          if (key === 'clear') {
            return (
              <button
                key={key}
                type="button"
                onClick={pressClear}
                disabled={verifying}
                aria-label="Clear PIN"
                className="h-14 rounded-xl border border-border bg-card text-xs font-medium text-muted-foreground hover:bg-muted/40 transition-colors disabled:opacity-50"
              >
                Clear
              </button>
            )
          }
          if (key === 'back') {
            return (
              <button
                key={key}
                type="button"
                onClick={pressBack}
                disabled={verifying}
                aria-label="Delete last digit"
                className="h-14 rounded-xl border border-border bg-card text-muted-foreground hover:bg-muted/40 transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                <Icon name="Delete" size={18} />
              </button>
            )
          }
          return (
            <button
              key={key}
              type="button"
              onClick={() => pressDigit(key)}
              disabled={verifying}
              className="h-14 rounded-xl border border-border bg-card text-lg font-semibold text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50"
            >
              {key}
            </button>
          )
        })}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={verifying || pin.length < minLength}
        className="w-full h-12 rounded-xl bg-primary/15 border border-primary/30 text-sm font-semibold text-foreground hover:bg-primary/25 transition-colors disabled:opacity-40"
      >
        {verifying ? 'Verifying…' : submitLabel}
      </button>
    </>
  )
}
